import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {IMenuCategory, IMenuItem, IMenuOptionChoice, IMenu, TrendingItemStore} from '../../store';
import {DeviceFileCacheService, IBaseResponse, WebService} from '../../services';
import {TItemLabelName} from '../../types';

export interface IMenuStoreState {
	menu: IMenu | null;
}

type TItemId = string | null;

@Module({name: 'fullMenu', namespaced: true})
class MenuModule extends VuexModule implements IMenuStoreState {
	menu: IMenu | null = null;
	/**
	 * retrieves the item identified by itemId
	 *
	 * If itemHqId is provided, this method will first attempt to look
	 * up itemByHqId before falling back to itemByMenu.
	 */
	get item() {
		return (itemId: TItemId, itemHqId: TItemId = null): IMenuItem | null => {
			if(itemHqId) {
				const hqItem = this.itemByHqId(itemHqId);
				if(hqItem !== null) {
					return hqItem;
				}
			}
			return this.itemByMenu(this.menu, itemId);
		};
	}

	get itemByMenu() {
		return (menu: IMenu | null, itemId: TItemId): IMenuItem | null => {
			if(!menu || !itemId) {
				return null;
			}
			return menu.items[itemId] || null;
		};
	}

	get itemByHqId() {
		return (hqItemId: TItemId): IMenuItem | null => {
			if(!this.menu || !hqItemId) {
				return null;
			}
			return Object.values(this.menu.items).find((menuItem) => {
				return menuItem.itemHqId === hqItemId;
			}) || null;
		};
	}

	get categories(): IMenuCategory[] {
		return this.categoriesByMenu(this.menu);
	}

	get categoriesByMenu() {
		return (menu: IMenu | null): IMenuCategory[] => {
			if(!menu) {
				return [];
			}
			return menu.categories;
		};
	}

	get category() {
		return (categoryId: string | null): IMenuCategory | null => {
			return this.categoryByMenu(this.menu, categoryId);
		};
	}

	get categoryByMenu() {
		return (menu: IMenu | null, categoryId: string | null): IMenuCategory | null => {
			if(!menu) {
				return null;
			}
			return menu.categories.find((c) => {
				return c.categoryId === categoryId;
			}) || null;
		};
	}

	get itemsByCategory() {
		return (categoryId: string): IMenuItem[] => {
			return this.itemsByCategoryByMenu(this.menu, categoryId);
		};
	}

	get itemsByCategoryByMenu() {
		return (menu: IMenu | null, categoryId: string): IMenuItem[] => {
			if(!menu) {
				return [];
			}
			const category = this.category(categoryId);
			if(!category) {
				return [];
			}
			const items: IMenuItem[] = [];
			for(const itemId of category.itemIds) {
				const item = menu.items[itemId];
				if(item && !item.outOfStock) {
					items.push(item);
				}
			}
			return items;
		};
	}

	get option() {
		return (optionId: string) => {
			return this.optionByMenu(this.menu, optionId);
		};
	}

	get optionByMenu() {
		return (menu: IMenu | null, optionId: string) => {
			if(!menu) {
				return null;
			}
			return menu.options[optionId] || null;
		};
	}

	get choice() {
		return (optionId: string, choiceId: string): IMenuOptionChoice | null => {
			return this.choiceByMenu(this.menu, optionId, choiceId);
		};
	}

	get choiceByMenu() {
		return (menu: IMenu | null, optionId: string, choiceId: string): IMenuOptionChoice | null => {
			if(!menu) {
				return null;
			}
			const option = this.option(optionId);
			if(!option) {
				return null;
			}
			return option.choices[choiceId] || null;
		};
	}

	get getChoiceImage() {
		return (optionId: string, choiceId: string): IMenuOptionChoice['image'] => {
			const choice = this.choice(optionId, choiceId);
			if(!choice) {
				return null;
			}
			return choice.image;
		};
	}

	get itemLabels() {
		return (itemId: string) => {
			const labels: TItemLabelName[] = [];
			if(this.menu && this.menu.items) {
				if(TrendingItemStore.isTrending(itemId)) {
					labels.push('trending');
				}
			}
			return labels;
		};
	}

	@Action
	async fetchMenu(menuId: string) {
		const response = await WebService.getOrderingClient().get<IBaseResponse<{menu: IMenu}>>(`menus/${menuId}`);
		this.setMenu(response.data.data.menu);
		DeviceFileCacheService.precacheObjectImages<IMenu>(response.data.data.menu).then(() => {
			console.log('Precached menu images');
		}, (error) => {
			console.error('Failed to precache menu images', error);
		});
	}

	@Mutation
	public setMenu(menu: IMenu) {
		/**
		 * Temporarily disable/remove bundles.
		 * First, remove all bundle line items and capture their IDs.
		 */
		const itemIDs = Object.keys(menu.items);
		const removedIDs: Array<IMenuItem['itemId']> = ['blank'];
		itemIDs.forEach((itemID) => {
			if(menu.items[itemID].lineItem?.type === 'bundle') {
				removedIDs.push(itemID);
				delete menu.items[itemID];
			}
		});
		/**
		 * Now, remove any bundle items from categories.
		 */
		removedIDs.forEach((itemID) => {
			menu.categories.forEach((category) => {
				category.itemIds = category.itemIds.filter((id) => {
					return itemID !== id;
				});
			});
		});

		this.menu = menu;
	}

	@Mutation
	public clearMenu() {
		this.menu = null;
	}
}

export {
	MenuModule
};
